import React, { createContext, useContext, useState } from 'react';

// =====================================================
// COMPONENTE TABS - Sistema de abas reutilizável
// =====================================================

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs.Root');
  }
  return context;
}

// =====================================================
// ROOT
// =====================================================

interface TabsRootProps {
  defaultValue: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

function TabsRoot({
  defaultValue,
  value,
  onValueChange,
  children,
  className = '',
}: TabsRootProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);

  const activeTab = value ?? internalValue;
  const setActiveTab = (tab: string) => {
    if (!value) {
      setInternalValue(tab);
    }
    onValueChange?.(tab);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

// =====================================================
// LIST (Container das tabs)
// =====================================================

interface TabsListProps {
  children: React.ReactNode;
  className?: string;
}

function TabsList({ children, className = '' }: TabsListProps) {
  return (
    <div
      role="tablist"
      className={`
        flex gap-1 p-1 rounded-xl bg-neutral-900/50 border border-neutral-700/50 overflow-x-auto
        ${className}
      `}
    >
      {children}
    </div>
  );
}

// =====================================================
// TRIGGER (Botão da tab)
// =====================================================

interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

function TabsTrigger({
  value,
  children,
  className = '',
  disabled = false,
}: TabsTriggerProps) {
  const { activeTab, setActiveTab } = useTabsContext();
  const isActive = activeTab === value;

  return (
    <button
      role="tab"
      type="button"
      aria-selected={isActive}
      disabled={disabled}
      onClick={() => !disabled && setActiveTab(value)}
      className={`
        flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap
        transition-all duration-200
        ${isActive
          ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
          : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
    >
      {children}
    </button>
  );
}

// =====================================================
// CONTENT (Conteúdo da tab)
// =====================================================

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

function TabsContent({ value, children, className = '' }: TabsContentProps) {
  const { activeTab } = useTabsContext();

  if (activeTab !== value) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      className={`animate-fade-in ${className}`}
    >
      {children}
    </div>
  );
}

// =====================================================
// EXPORT COMPOSTO
// =====================================================

export const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
};

// Adiciona animação de fade-in via CSS (adicionar ao index.css ou usar inline)
// @keyframes fade-in {
//   from { opacity: 0; transform: translateY(4px); }
//   to { opacity: 1; transform: translateY(0); }
// }
// .animate-fade-in {
//   animation: fade-in 0.2s ease-out;
// }
